/**
 * JournalLockGate — PIN gate shown when the (hidden) Journal is revealed.
 *
 * Renders one of:
 * - A "set PIN" screen if no PIN has been configured yet.
 * - An "enter PIN" screen if a PIN exists and the journal is locked.
 * - The provided children (the Journal module) once unlocked.
 *
 * The actual lock/PIN logic lives in journalLockStore. The PIN is stored only
 * as a salted hash locally; the raw PIN never leaves this component.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useJournalLockStore } from '@/stores/journalLockStore';

interface JournalLockGateProps {
  children: ReactNode;
}

export function JournalLockGate({ children }: JournalLockGateProps) {
  const initialized = useJournalLockStore((s) => s.initialized);
  const locked = useJournalLockStore((s) => s.locked);
  const hasPin = useJournalLockStore((s) => s.hasPin);
  const error = useJournalLockStore((s) => s.error);
  const initialize = useJournalLockStore((s) => s.initialize);
  const setPin = useJournalLockStore((s) => s.setPin);
  const unlock = useJournalLockStore((s) => s.unlock);
  const hide = useJournalLockStore((s) => s.hide);
  const clearError = useJournalLockStore((s) => s.clearError);

  const [pin, setPinInput] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!initialized) void initialize();
  }, [initialized, initialize]);

  // Unlocked → render the journal.
  if (initialized && !locked) {
    return <>{children}</>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();

    if (!hasPin) {
      if (pin.trim().length < 4) {
        setLocalError('PIN must be at least 4 digits.');
        return;
      }
      if (pin !== confirmPin) {
        setLocalError('PINs do not match.');
        return;
      }
      await setPin(pin);
      setPinInput('');
      setConfirmPin('');
      return;
    }

    const ok = await unlock(pin);
    if (!ok) {
      setPinInput('');
    }
  };

  const shownError = localError ?? error;

  return (
    <div className="flex-1 h-full flex items-center justify-center p-6">
      <div className="card-gradient w-full max-w-sm p-8 flex flex-col items-center gap-5 text-center">
        <span className="text-5xl" aria-hidden="true">🔒</span>
        <div>
          <h2 className="text-heading-3 font-bold text-white">
            {hasPin ? 'Journal Locked' : 'Secure Your Journal'}
          </h2>
          <p className="text-text-secondary text-sm mt-1">
            {hasPin
              ? 'Enter your PIN to open your private journal.'
              : 'Set a PIN to keep your journal private. You will need it each time.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
          <input
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPinInput(e.target.value)}
            placeholder={hasPin ? 'Enter PIN' : 'Create a PIN (min 4 digits)'}
            aria-label={hasPin ? 'Enter PIN' : 'Create a PIN'}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-center tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-amber-400/50"
          />

          {!hasPin && (
            <input
              type="password"
              inputMode="numeric"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              placeholder="Confirm PIN"
              aria-label="Confirm PIN"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-center tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-amber-400/50"
            />
          )}

          {shownError && (
            <p className="text-sm text-red-400" role="alert">{shownError}</p>
          )}

          <button
            type="submit"
            className="btn-primary text-white px-6 py-3 text-body font-semibold w-full"
          >
            {hasPin ? 'Unlock' : 'Set PIN & Open'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => hide()}
          className="text-text-tertiary text-xs hover:text-text-secondary transition-colors"
        >
          Hide journal (Esc)
        </button>
      </div>
    </div>
  );
}

export default JournalLockGate;
